import { KardexFilters, KardexReportData, KardexEntry } from '@/types/kardex.types';
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface Ingredient {
  id: string;
  name: string;
  description: string;
  category: string;
  unit: string;
  globalCostPerUnit: number;
  isActive: boolean;
  locationStocks?: {
    locationId: string;
    locationName: string;
    currentStock: number;
    minStock: number;
    maxStock: number;
    costPerUnit: number;
    expirationDate?: string;
    lastRestockDate?: string;
    isActive: boolean;
  }[];
}

export interface StockMovement {
  id: string;
  ingredientId: string;
  locationId: string;
  direction: 'entry' | 'exit'; // Dirección del movimiento
  type: 'purchase' | 'sale' | 'adjustment' | 'waste' | 'transfer' | 'merchandise' | 'transformation';
  sourceType?: 'SALE' | 'PURCHASE' | 'TRANSFER' | 'MERCHANDISE' | 'TRANSFORMATION' | 'ADJUSTMENT' | 'WASTE';
  quantity: number;
  previousStock: number;
  newStock: number;
  unitCost: number;
  totalCost: number;
  reason: string;
  referenceId?: string;
  userId?: string;
  createdAt: string;
}

// Obtener todos los ingredientes
export const fetchIngredients = async (): Promise<Ingredient[]> => {
  try {
    const response = await authFetch(`${API_BASE_URL}/ingredients?limit=1000`);

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const result = await response.json();
    return result.success ? result.data : [];
  } catch (error) {
    console.error('Error fetching ingredients:', error);
    throw error;
  }
};

// Obtener movimientos de un ingrediente
export const fetchIngredientMovements = async (
  ingredientId: string,
  filters?: {
    dateFrom?: string;
    dateTo?: string;
    type?: string;
    locationId?: string;
  }
): Promise<StockMovement[]> => {
  try {
    const params = new URLSearchParams();
    params.append('limit', '1000');

    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.type && filters.type !== 'all') params.append('type', filters.type);
    if (filters?.locationId && filters.locationId !== 'all') params.append('locationId', filters.locationId);

    const response = await authFetch(`${API_BASE_URL}/ingredients/${ingredientId}/movements?${params}`);

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const result = await response.json();
    return result.success ? result.data : [];
  } catch (error) {
    console.error('Error fetching ingredient movements:', error);
    return [];
  }
};

// Resultado del saldo de cierre del turno anterior
export interface PreviousShiftBalanceResult {
  closingBalance: number;
  date: string;
  shiftType: string;
}

// Obtener saldo de cierre del turno anterior
export const fetchPreviousShiftBalance = async (
  locationId: string,
  ingredientId: string,
  date: string,
  shift?: string
): Promise<PreviousShiftBalanceResult | null> => {
  try {
    const params = new URLSearchParams();
    params.append('locationId', locationId);
    params.append('ingredientId', ingredientId);
    params.append('date', date);
    if (shift) params.append('shift', shift);

    const response = await authFetch(`${API_BASE_URL}/shift-stock-balances/previous?${params}`);

    if (!response.ok) {
      return null;
    }

    const result = await response.json();
    if (result.success && result.data) {
      return {
        closingBalance: result.data.closingBalance,
        date: result.data.date,
        shiftType: result.data.shiftType
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching previous shift balance:', error);
    return null;
  }
};

// Obtener ubicaciones/locales
export const fetchLocations = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/locations/all`);

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const result = await response.json();

    if (result.success && result.data && Array.isArray(result.data)) {
      const locations = result.data.map((loc: any) => ({
        id: loc.id.toString(),
        nombre: loc.nombre
      }));
      return [{ id: 'all', nombre: 'Todos los locales' }, ...locations];
    }

    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [{ id: 'all', nombre: 'Todos los locales' }];
  }
};

// Generar reporte kardex real basado en movimientos de la API
export const generateKardexReport = async (filters: KardexFilters): Promise<KardexReportData | null> => {
  try {
    // Obtener el ingrediente
    const ingredients = await fetchIngredients();
    const ingredient = ingredients.find(ing => String(ing.id) === String(filters.ingredientId));

    if (!ingredient) {
      console.error('Ingrediente no encontrado');
      return null;
    }

    // Obtener stock para la ubicación (comparar como strings para evitar problemas de tipos)
    const locationStock = ingredient.locationStocks?.find(
      stock => String(stock.locationId) === String(filters.locationId)
    );

    if (!locationStock && filters.locationId !== 'all') {
      console.error('Stock para esta ubicación no encontrado:', filters.locationId);
      return null;
    }

    // Obtener movimientos
    const movements = await fetchIngredientMovements(filters.ingredientId, {
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      locationId: filters.locationId
    });

    // Filtrar movimientos por ubicación específica (comparar como strings)
    const filteredMovements = movements.filter(mov =>
      filters.locationId === 'all' || String(mov.locationId) === String(filters.locationId)
    );

    // Filtrar por turno si se especifica
    let shiftFilteredMovements = filteredMovements;
    if (filters.shift && filters.shift !== 'all') {
      // Para la API real, asumimos que el turno se puede derivar de la hora
      shiftFilteredMovements = filteredMovements.filter(mov => {
        const hour = new Date(mov.createdAt).getHours();
        if (filters.shift === 'morning') {
          return hour >= 6 && hour < 14; // 6 AM - 2 PM
        } else if (filters.shift === 'afternoon') {
          return hour >= 14 && hour < 22; // 2 PM - 10 PM
        }
        return true;
      });
    }

    // Obtener saldo inicial del turno anterior (si existe)
    const shiftMap: Record<string, string> = {
      'morning': 'MAÑANA',
      'afternoon': 'TARDE',
      'all': ''
    };
    const shiftType = filters.shift ? shiftMap[filters.shift] || '' : '';

    // Intentar obtener el saldo de cierre del turno anterior
    let initialBalance: number;
    let initialBalanceDate: string | undefined;
    const previousShiftBalanceResult = await fetchPreviousShiftBalance(
      filters.locationId,
      filters.ingredientId,
      filters.dateFrom,
      shiftType
    );

    if (previousShiftBalanceResult !== null) {
      // Usar el saldo de cierre del turno anterior como saldo inicial
      initialBalance = previousShiftBalanceResult.closingBalance;
      initialBalanceDate = previousShiftBalanceResult.date;
    } else {
      // Si no hay cierre registrado, mostrar 0
      initialBalance = 0;
    }

    // Primero ordenar los movimientos cronológicamente (de más antiguo a más reciente)
    const sortedMovements = [...shiftFilteredMovements].sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    // Convertir movimientos y calcular saldo acumulativo
    let runningBalance = initialBalance;
    const entries: KardexEntry[] = sortedMovements.map(movement => {
      const date = new Date(movement.createdAt);

      // Usar el campo direction para determinar si es entrada o salida
      const isEntry = movement.direction === 'entry';
      const quantity = Math.abs(movement.quantity);

      // Calcular saldo acumulativo
      if (isEntry) {
        runningBalance += quantity;
      } else {
        runningBalance -= quantity;
      }

      return {
        id: movement.id,
        date: date.toISOString().split('T')[0],
        timestamp: movement.createdAt, // Para ordenar por fecha y hora
        movementType: movement.type,
        reference: movement.referenceId || movement.id,
        description: getMovementDescription(movement.type, movement.reason),
        quantity: quantity,
        unit: ingredient.unit,
        inQuantity: isEntry ? quantity : undefined,
        outQuantity: !isEntry ? quantity : undefined,
        balance: runningBalance, // Saldo calculado acumulativamente
        performedBy: movement.userId || 'Sistema'
      };
    });

    // Calcular totales
    const totalIn = entries.reduce((sum, entry) => sum + (entry.inQuantity || 0), 0);
    const totalOut = entries.reduce((sum, entry) => sum + (entry.outQuantity || 0), 0);

    // El saldo final es el último saldo calculado (o el inicial si no hay movimientos)
    const finalBalance = entries.length > 0 ? entries[entries.length - 1].balance : initialBalance;

    return {
      ingredientId: ingredient.id,
      ingredientName: ingredient.name,
      locationId: filters.locationId,
      locationName: filters.locationId === 'all' ? 'Todos los locales' : (locationStock?.locationName || 'Ubicación'),
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      unit: ingredient.unit,
      initialBalance,
      initialBalanceDate,
      finalBalance,
      totalIn,
      totalOut,
      currentStock: locationStock?.currentStock || 0,
      entries // Ya están ordenados cronológicamente
    };

  } catch (error) {
    console.error('Error generating kardex report:', error);
    return null;
  }
};

// Helper para describir tipos de movimiento
const getMovementDescription = (type: string, reason?: string): string => {
  if (reason) return reason;

  const descriptions = {
    purchase: 'Compra a proveedor',
    sale: 'Venta - consumo en receta',
    adjustment: 'Ajuste de inventario',
    waste: 'Merma por deterioro',
    return: 'Devolución de cliente',
    transfer_in: 'Transferencia desde otra sucursal',
    transfer_out: 'Transferencia a otra sucursal',
    merchandise: 'Movimiento de mercadería',
    transformation: 'Transformación de insumos'
  };

  return descriptions[type as keyof typeof descriptions] || 'Movimiento de stock';
};
